import 'focus-visible';
import create from '@verndale/core';
import modules from './modules';
import { initButtonsAnimation } from './helpers/buttonsAnimations';
import { initFadeInUpAnimation } from './helpers/fadeInUpAnimations';
import { prefersReducedMotion } from './helpers/index';
import { initTitlesAnimation } from './helpers/titlesAnimations';
import { initStatsCounters } from './helpers/statsCounters';

const isStorybook = !!document.getElementById('storybook-root');
const body = document.querySelector('body') as HTMLBodyElement;

/**
 * Checks if a URL is external (different origin than current page)
 */
const isExternalUrl = (url: string): boolean => {
  try {
    const linkUrl = new URL(url, window.location.href);
    return linkUrl.origin !== window.location.origin;
  } catch {
    // If URL parsing fails, treat as internal
    return false;
  }
};

/**
 * Adds external link icons to buttons that link to external URLs
 */
const addExternalLinkIcons = (): void => {
  const buttons = document.querySelectorAll<HTMLAnchorElement>('a.btn');

  buttons.forEach((button) => {
    const href = button.getAttribute('href');
    if (!href) return;

    // Skip if already has an external link icon
    if (button.querySelector('.icon[data-external-link-icon]')) return;

    // Skip if button has a download icon
    const downloadIcon = button.querySelector('use');
    if (downloadIcon) {
      const xlinkHref = downloadIcon.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
      if (xlinkHref === '#download') return;
    }

    // Check if it's an external link
    const isExternal = isExternalUrl(href);

    if (isExternal) {
      // Create the external link icon SVG
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('viewBox', '0 0 12 12');
      icon.setAttribute('class', 'icon');
      icon.setAttribute('data-external-link-icon', 'true');
      icon.setAttribute('role', 'presentation');
      icon.setAttribute('aria-hidden', 'true');
      icon.style.width = '12px';
      icon.style.height = '12px';
      icon.style.marginInlineStart = '4px';
      icon.style.flexShrink = '0';

      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#external-link');
      icon.appendChild(use);

      // Check if button has animation structure
      const textWrapper = button.querySelector('.btn__text-wrapper');
      if (textWrapper) {
        // Add icon to both text spans for animation
        const textSpans = textWrapper.querySelectorAll('.btn__text');
        textSpans.forEach((textSpan) => {
          const iconClone = icon.cloneNode(true) as SVGElement;
          textSpan.appendChild(iconClone);
        });
      } else {
        // Append icon directly to button if no animation structure
        button.appendChild(icon);
      }
    }
  });
};

document.addEventListener(
  'DOMContentLoaded',
  async () => {
    if (!body.dataset.modulesLoaded || isStorybook) {
      create(modules).then(() => {
        body.dataset.modulesLoaded = 'true';
        // Add external link icons after modules are loaded
        addExternalLinkIcons();
      });

      // Prepare counters before the shared reveal collects its child cards.
      initStatsCounters();

      // Only initialize animations once
      if (!prefersReducedMotion()) {
        // Animate all fade-in-up elements (single and group)
        initFadeInUpAnimation('.fade-in-up');
        initTitlesAnimation();

        // Buttons can animate immediately
        initButtonsAnimation();
      }

      // Add external link icons to buttons on initial load
      addExternalLinkIcons();
    }
  },
  false
);
